/* Copyright (c) 2020-present CloudIO Inc */

import { hasAutoLayoutParent } from 'componentregistry/utils/ManagerUtils';
import { isEmpty, isNotEmpty } from 'utils/isEmpty';
import { Platform } from '../../platform/Platform';
import {
  AliasType,
  ComponentManagerFn,
  RequiredItem,
  ViewAttributeChooserProp,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { baseFieldProps } from '../TextField/BaseField';
import { defaultValue } from './RichTextEditor';
import { helpRenders } from './RichTextEditor_Help';
import { toolbarLayoutRTE } from 'componentregistry/registry/constants';

const RichTextEditorManager: ComponentManagerFn<'RichTextEditor'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'RichTextEditor'>(platform, 'RichTextEditor'),
    type: 'RichTextEditor',
    name: 'Rich Text Editor',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnChange', 'TriggerOnValueChange'] as ItemType[]).includes(
        childType,
      ),
    defaultValue,
    props: {
      ...baseFieldProps,
      viewAttribute: {
        ...(baseFieldProps.viewAttribute as ViewAttributeChooserProp),
        getAttributeDataTypes: () => ['JSON'],
        required: true,
      },
      htmlAttribute: {
        label: 'HTML View Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['String', 'Textarea'],
        preventMultiEdit: true,
        helpText:
          'Make Sure Attribute Max Length is sufficient to store HTML content.',
      },
      textAttribute: {
        label: 'Text View Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['String', 'Textarea'],
        preventMultiEdit: true,
        helpText:
          'Make Sure Attribute Max Length is sufficient to store  text content.',
      },
      placeholder: {
        label: 'Placeholder',
        type: 'string',
      },
      editorHeight: {
        label: 'Height',
        type: 'numberInput',
      },
      optionValues: {
        type: 'options',
        label: 'Option Values',
        category: 'Mentions',
        helpText:
          'If the options are fixed, then you can specify here instead of providing a Data Store Alias below. e.g. B:Blue, G:Green, Yellow, Red',
      },
      mentionDatasourceAlias: {
        label: 'Data Store Alias',
        type: 'DataSourceAliasChooser',
        category: 'Mentions',
        allowedTypes: ['CloudDataStore', 'ReadOnlyDataStore', 'LocalDataStore'],
      },
      valueAttribute: {
        label: 'Value Attribute',
        type: 'ComboAttributeChooser',
        aliasType: 'LOV',
        category: 'Mentions',
        required: (item: RequiredItem<'RichTextEditor'>) =>
          isNotEmpty(item.props.mentionDatasourceAlias),
        getAttributeDataTypes: () => 'ALL',
      },
      labelAttribute: {
        aliasType: 'LOV',
        getAttributeDataTypes: () => 'ALL',
        label: 'Label Attribute',
        category: 'Mentions',
        required: (item: RequiredItem<'RichTextEditor'>) =>
          isNotEmpty(item.props.mentionDatasourceAlias),
        type: 'ComboAttributeChooser',
      },
      border: {
        label: 'Border',
        type: 'border',
        category: 'Look & Feel',
        helpText:
          'Select here to apply the borders on RTE.This border size will be included in height/width',
      },
      toolbarLayout: {
        label: 'Toolbar Layout',
        type: 'select',
        category: 'Look & Feel',
        options: toolbarLayoutRTE,
        helpText: 'Select here to change the Toolbar position.',
      },
    },
    isPropertyVisible: (property, itemData, context) => {
      switch (property) {
        case 'optionValues':
          return isEmpty(itemData.mentionDatasourceAlias);
        case 'mentionDatasourceAlias':
          return isEmpty(itemData.optionValues);
        case 'valueAttribute':
        case 'labelAttribute':
          return isNotEmpty(itemData.mentionDatasourceAlias);
        case 'editorHeight': {
          const page = platform.getPage(context.appUid, context.pageId);
          return hasAutoLayoutParent(page, page[context.itemId]).result;
        }
        case 'colorExpression':
        case 'bgColorExpression':
          return false;
        default:
          return true;
      }
    },
    replaceableWith: ['TextField'],
    getDataSourceAliasProperty: (aliasType: AliasType) =>
      aliasType === 'LOV' ? { aliasProp: 'mentionDatasourceAlias' } : undefined,
    isDataSourceAliasUsed: (item, alias) =>
      item.props.mentionDatasourceAlias === alias,
    getDisplayText: (item) =>
      typeof item.props.label === 'string'
        ? item.props.label
        : item.props.label?.value ?? '',
    category: 'Data',
    icon: { iconName: 'io-text-input', iconType: 'cloudio' },
    description: `**Overview**<br/>
The Rich Text Editor (RTE) is a sophisticated input component that allows users to author formatted content—including bold text, lists, links, and user mentions.
Unlike a standard Text Field which stores simple strings, the RTE handles structured content. It is designed for "Authoring" workflows, such as writing email templates, project descriptions, or knowledge base articles, where preserving the structure and formatting of the input is critical.

**1. Data Storage Strategy (Critical)**<br/>
Because rich text is complex, this component uses a multi-layered storage approach to ensure data integrity while remaining useful for other parts of your application.
* **View Attribute (Primary Source):** This field is Mandatory. It stores the content in a structured JSON format. This is the "Source of Truth" used to reload the editor for future editing without data loss.
* **HTML Attribute (Projection):** Optional. Stores the content as standard HTML. Use this field when you need to display the content in a read-only view (like an email body) or send it to an external API. Do not use this for re-editing.
* **Text Attribute (Projection):** Optional. Stores the content as plain text, stripping all formatting. Essential for search indexing or displaying summaries (e.g., "First 50 characters...") in a data grid.

**2. Mentions System**<br/>
The RTE supports "At-Mentions" (typing @ to select a user or item). You must choose one of two ways to populate the mention list:
* **Option A: Static Mentions:** Best for small, fixed lists like status codes or priority tags. (Enter a comma-separated list in Option Values).
* **Option B: Dynamic Mentions:** Best for large, changing datasets like Users, Products, or Tickets.
    * **Data Store Alias:** Select the Data Store containing the records.
    * **Label Attribute:** The text the user searches for and sees in the editor (e.g., EmployeeName).
    * **Value Attribute:** The unique ID stored in the JSON data (e.g., EmployeeID). This ensures that even if a user changes their name, the link remains valid.

**3. Layout & Toolbar**<br/>
You can adapt the editor's interface to fit the density of your page.
* **Toolbar Layout:** Controls the position of the formatting bar. (Top / Bottom are standard fixed positions; Inline appears floating near the cursor when text is selected).
* **Height:**
    * **Auto Layout:** If placed in a flexible container, you can set a specific numeric height.
    * **Fixed Layout:** If placed in a fixed layout, the parent container dictates the size.

**4. Interaction & Triggers**<br/>
Since the RTE is a data input tool, it supports standard field lifecycle triggers.
* **TriggerOnChange / TriggerOnValueChange:** Execute logic whenever the user modifies the content. This is useful for "Auto-Save" functionality or enabling/disabling a "Post" button based on whether the editor is empty.

**5. Best Practices**<br/>
* **Interchangeability:** If you start with a simple TextField and later realize you need formatting, you can use the Replace With feature to swap it for a Rich Text Editor. The data binding will be preserved, though you may need to map the new JSON attribute.`,
    helpRenders,
  };
};

export default RichTextEditorManager;
